import { FC, FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { normalize } from '@/form/fields/_pipeline/normalize.ts';
import { useDebouncedValue } from './useDebouncedValue';
import { useSearchAddress } from '@/searchAddress/useSearchAddress.ts';
import { LocationPredictionResponse } from '@/searchAddress/AddressTypes.ts';
import { useUpdateAdressFields } from '@/form/fields/AddressFinder/useUpdateAdressFields.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { i18n } from '@/translation/TranslationProvider.tsx';
import { translate } from '@/translation/translate.ts';
import poweredByGoogleLogo from '@/assets/images/powered_by_google_on_white.png';

type Option = { value: string; label: string };

type Props = {
  field: any;
  slideId: any;
  onChange: any;
  getFieldStatus: any;
};

export const AddressFinder: FC<Props> = ({ field, slideId, onChange, getFieldStatus }) => {
  const { searchAddress } = useSearchAddress();
  const { updateAddressFields } = useUpdateAdressFields();
  const { eventConfig } = useEventConfig();

  const fieldStatus = getFieldStatus(field.name);
  const placeholder = translate(field.placeholder);

  const [query, setQuery] = useState(fieldStatus?.value?.raw ?? '');
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [locationPredictionResponse, setLocationPredictionResponse] = useState<LocationPredictionResponse>();
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncedValue(query, 200);

  const fetchAddressOptions = async (q: string): Promise<Option[]> => {
    const response = await searchAddress({ query: q, country: eventConfig?.meta.country, language: i18n.locale });

    setLocationPredictionResponse(response);

    return response.predictions.map((prediction) => ({
      value: prediction.id,
      label: prediction.description,
    }));
  };

  function hasOption(label: string): boolean {
    return options.some((option) => option.label === label);
  }

  useEffect(() => {
    if (hasOption(debouncedQuery)) return;
    if (!debouncedQuery || debouncedQuery.length < 3) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptions([]);
      return;
    }

    setLoading(true);

    fetchAddressOptions(debouncedQuery)
      .then(setOptions)
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [debouncedQuery]);

  const commit = async (opt: Option) => {
    const raw = opt.label;
    const normalized = normalize(field.datatype, raw);
    const value = { raw, normalized };

    onChange(field.name, { value, hint: undefined, isValid: false, slideId });

    await updateAddressFields(opt.value, eventConfig?.meta.country, i18n.locale, locationPredictionResponse?.session);

    onChange(field.name, { value, hint: undefined, isValid: true, slideId });
    setOpen(false);
    setQuery(opt.label);
  };

  const clearInput = () => {
    setQuery('');
    setOptions([]);
    setOpen(false);
    setActiveIndex(-1);
    setLocationPredictionResponse(undefined);

    onChange(field.name, {
      value: { raw: '', normalized: '' },
      hint: undefined,
      isValid: true,
      slideId,
    });

    inputRef.current?.focus();
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const nextFocusedElement = e.relatedTarget as HTMLElement | null;
    if (nextFocusedElement?.getAttribute('role') === 'option') return;

    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev >= options.length - 1 ? 0 : prev + 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
      return;
    }

    if ((e.key === 'Enter' || e.key === ' ') && activeIndex >= 0 && open) {
      e.preventDefault();
      void commit(options[activeIndex]);
      return;
    }

    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
  ];

  const showClearButton = !!query && !loading;
  const onClearButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Enter' && e.key !== 'NumpadEnter' && e.key !== ' ' && e.key !== 'Spacebar') return;

    e.preventDefault();
    clearInput();
  };

  return (
    <div className={cssClasses.join(' ')}>
      <label htmlFor={'id_field_' + field.name}>{translate(field.label)}</label>

      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          id={'id_field_' + field.name}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={'id_field_' + field.name + '_listbox'}
          aria-activedescendant={activeIndex >= 0 ? 'id_field_' + field.name + '_option_' + activeIndex : undefined}
          placeholder={placeholder}
          style={{
            width: '100%',
            paddingRight: loading || showClearButton ? 36 : undefined,
          }}
        />

        {showClearButton && (
          <button
            type="button"
            data-skip-enter-nav
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearInput}
            onKeyDown={onClearButtonKeyDown}
            aria-label="Input löschen"
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              width: 20,
              height: 20,
              border: 0,
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        )}

        {loading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              width: 16,
              height: 16,
              border: '2px solid #ccc',
              borderTop: '2px solid #333',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              pointerEvents: 'none',
            }}
          />
        )}

        {open && (
          <div
            id={'id_field_' + field.name + '_listbox'}
            role="listbox"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              border: '1px solid #ccc',
              background: 'white',
              zIndex: 10,
              maxHeight: 220,
              overflow: 'hidden',
            }}
          >
            {!loading &&
              options.map((o, index) => (
                <button
                  key={o.value}
                  id={'id_field_' + field.name + '_option_' + index}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onFocus={() => setActiveIndex(index)}
                  onClick={() => commit(o)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: 8,
                    background: index === activeIndex ? '#f5f5f5' : 'transparent',
                    border: 0,
                    cursor: 'pointer',
                  }}
                >
                  {o.label}
                </button>
              ))}
            {options.length !== 0 && eventConfig?.meta.country?.toUpperCase() !== 'GB' && (
              <img
                src={poweredByGoogleLogo}
                alt="Powered by Google"
                style={{ display: 'block', width: '7em', padding: 8 }}
              />
            )}
          </div>
        )}

        <style>
          {`
            @keyframes spin {
              from {
                transform: translateY(-50%) rotate(0deg);
              }
              to {
                transform: translateY(-50%) rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    </div>
  );
};
